import { useEffect, useRef } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'

import { Heading } from 'Components/_theme/Heading/Heading'
import { TertiaryButton } from 'Components/_theme/TertiaryButton/TertiaryButton'
import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'

import {
  ENTER_LABEL_MAP,
  IDENTIFIER_TYPE_OPTIONS,
  PLACEHOLDER_MAP,
} from './PublicRequestReturnLabel.constants'
import type {
  DeviceIdentifierBlockProps,
  PublicRequestReturnLabelFormValues,
} from './PublicRequestReturnLabel.types'

export const DeviceIdentifierBlock = (props: DeviceIdentifierBlockProps) => {
  const { index, showRemove, onRemove } = props
  const intl = useIntl()
  const { control, setValue } =
    useFormContext<PublicRequestReturnLabelFormValues>()
  const selectedType = useWatch({
    control,
    name: `devices.${index}.identifierType`,
  })
  const prevType = useRef(selectedType)

  useEffect(() => {
    if (prevType.current !== selectedType) {
      setValue(`devices.${index}.value`, '')
      prevType.current = selectedType
    }
  }, [selectedType, index, setValue])

  const title = showRemove ? (
    <FormattedMessage
      id="PublicRequestReturnLabel_deviceIdentifier_titleNumbered"
      values={{ number: index + 1 }}
    />
  ) : (
    <FormattedMessage id="PublicRequestReturnLabel_deviceIdentifier_title" />
  )

  const stepTwoLabel = `2. ${intl.formatMessage({ id: ENTER_LABEL_MAP[selectedType] })}`
  const placeholder = intl.formatMessage({ id: PLACEHOLDER_MAP[selectedType] })

  return (
    <div className="DeviceIdentifierBlock p-6">
      <div className="DeviceIdentifierBlock-header d-flex align-items-center justify-content-between mb-3">
        <Heading as={4} className="m-0" size={3}>
          {title}
        </Heading>
        {showRemove && (
          <TertiaryButton
            aria-label={intl.formatMessage({
              id: 'PublicRequestReturnLabel_deviceIdentifier_removeDevice',
            })}
            asIcon={faXmark}
            onClick={onRemove}
          />
        )}
      </div>

      <Heading as={6} className="mb-2" size={6}>
        1.{' '}
        <FormattedMessage id="PublicRequestReturnLabel_deviceIdentifier_selectType" />
      </Heading>
      <div
        aria-label={intl.formatMessage({
          id: 'PublicRequestReturnLabel_deviceIdentifier_selectType',
        })}
        className="d-flex align-items-center gap-4 flex-wrap"
        role="radiogroup"
      >
        {IDENTIFIER_TYPE_OPTIONS.map(option => (
          <Field
            key={option.type}
            label={option.labelId}
            name={`devices.${index}.identifierType`}
            type={FieldTypeEnum.radio}
            value={option.type}
          />
        ))}
      </div>

      <Field
        autoComplete="off"
        containerClassName="mt-4"
        label={stepTwoLabel}
        labelClassName="h6 fw-bold mb-2"
        markAsRequired
        name={`devices.${index}.value`}
        placeholder={placeholder}
        shouldTranslateLabel={false}
        type={FieldTypeEnum.text}
      />
    </div>
  )
}
